import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a cookie choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      essential: true,
      performance: true,
      functional: true,
      marketing: true,
    }));
    setShowBanner(false);
  };

  const handleRejectNonEssential = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      essential: true,
      performance: false,
      functional: false,
      marketing: false,
    }));
    setShowBanner(false);
  };

  const handleClose = () => {
    // Closing without choice defaults to essential only
    localStorage.setItem('cookieConsent', 'essential-only');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      essential: true,
      performance: false,
      functional: false,
      marketing: false,
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
      data-testid="cookie-consent-banner"
    >
      <div className="container-width">
        <div className="bg-white rounded-2xl shadow-2xl border border-border p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-12 h-12 rounded-full bg-primary/10 items-center justify-center flex-shrink-0">
              <Cookie className="h-6 w-6 text-primary" />
            </div>
            
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    We Value Your Privacy
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    We use cookies to enhance your browsing experience, serve personalized content, and analyze 
                    our traffic. By clicking "Accept All", you consent to our use of cookies. You can also choose 
                    to reject non-essential cookies or learn more about our cookie practices.
                  </p>
                </div>
                <button 
                  onClick={handleClose}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1"
                  aria-label="Close cookie banner"
                  data-testid="cookie-close-btn"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-6"
                  data-testid="cookie-accept-btn"
                >
                  Accept All
                </Button>
                <Button
                  onClick={handleRejectNonEssential}
                  variant="outline"
                  className="rounded-full px-6"
                  data-testid="cookie-reject-btn"
                >
                  Reject Non-Essential
                </Button>
                <Link 
                  to="/cookie-policy"
                  className="text-sm text-primary hover:underline"
                  data-testid="cookie-learn-more-link"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
